import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { testBtnPrimary } from '../test/testUiClasses';
import {
  computeGuideBubbleLayout,
  DEFAULT_BUBBLE_HEIGHT,
  DEFAULT_BUBBLE_WIDTH
} from './tutorialOverlayLayout';

function GuideBubble({
  title,
  body,
  onNext,
  onSkip,
  nextLabel,
  skipLabel,
  placement = 'bottom',
  arrowOffset = 32
}) {
  const arrowStyle =
    placement === 'left' || placement === 'right'
      ? { top: '50%' }
      : { left: `${arrowOffset}px` };

  const arrowClass =
    placement === 'top'
      ? 'bottom-[-7px] border-b border-r'
      : placement === 'left'
        ? 'right-[-7px] -translate-y-1/2 border-t border-r'
        : placement === 'right'
          ? 'left-[-7px] -translate-y-1/2 border-b border-l'
          : 'top-[-7px] border-t border-l';

  return (
    <div className="w-72 max-w-[calc(100vw-2rem)]" role="dialog" aria-live="polite">
      <div className="relative bg-surface rounded-2xl shadow-xl border border-edge p-4 text-left">
        <div
          className={`absolute w-3 h-3 bg-surface border-edge rotate-45 ${arrowClass}`}
          style={arrowStyle}
        />
        <h3 className="text-sm font-bold text-ink mb-1">{title}</h3>
        <p className="whitespace-pre-line text-sm leading-relaxed text-ink-muted">{body}</p>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onSkip} className="px-3 py-1.5 text-sm text-ink-muted hover:text-ink">
            {skipLabel}
          </button>
          <button type="button" onClick={onNext} className={testBtnPrimary}>
            {nextLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function AnchoredTutorialStep({
  anchorEl,
  current,
  onFinish,
  onNext,
  nextLabel,
  skipLabel,
  highlightPad = 8
}) {
  const bubbleRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const [settled, setSettled] = useState(() => !current.anchorSettleMs);

  const updateLayout = useCallback(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const bubbleWidth = bubbleRef.current?.offsetWidth || DEFAULT_BUBBLE_WIDTH;
    const bubbleHeight = bubbleRef.current?.offsetHeight || DEFAULT_BUBBLE_HEIGHT;
    setLayout(
      computeGuideBubbleLayout(rect, current.placement || 'bottom', bubbleWidth, bubbleHeight)
    );
  }, [anchorEl, current.placement]);

  useLayoutEffect(() => {
    if (!anchorEl) return undefined;

    const scrollBlock = current.scrollBlock || 'nearest';
    if (current.scrollIntoView !== false) {
      anchorEl.scrollIntoView({ block: scrollBlock, behavior: 'smooth', inline: 'nearest' });
    }

    updateLayout();
    const raf = requestAnimationFrame(updateLayout);
    const layoutTimers = [100, 300, 650].map((ms) => window.setTimeout(updateLayout, ms));

    return () => {
      cancelAnimationFrame(raf);
      layoutTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [anchorEl, updateLayout, current.title, current.body, current.scrollBlock, current.scrollIntoView]);

  useEffect(() => {
    if (!current.anchorSettleMs) {
      setSettled(true);
      return undefined;
    }

    setSettled(false);
    const timer = window.setTimeout(() => {
      updateLayout();
      setSettled(true);
    }, current.anchorSettleMs);
    return () => window.clearTimeout(timer);
  }, [current.anchorSettleMs, updateLayout]);

  useEffect(() => {
    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);
    return () => {
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
    };
  }, [updateLayout]);

  const rect = anchorEl.getBoundingClientRect();
  const highlightStyle = {
    top: rect.top - highlightPad,
    left: rect.left - highlightPad,
    width: rect.width + highlightPad * 2,
    height: rect.height + highlightPad * 2
  };

  const bubbleTop = layout?.top ?? rect.bottom + 12;
  const bubbleLeft = layout?.left ?? Math.max(16, rect.left);
  const placement = layout?.placement ?? (current.placement || 'bottom');

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none">
      <div className="absolute inset-0 bg-black/45 pointer-events-auto" onClick={onFinish} aria-hidden />
      {settled && current.showHighlight !== false ? (
        <div
          className="absolute rounded-xl ring-4 ring-blue-500 ring-offset-2 ring-offset-transparent pointer-events-none"
          style={highlightStyle}
        />
      ) : null}
      <div
        ref={bubbleRef}
        className="fixed z-[61] pointer-events-auto"
        style={{
          top: bubbleTop,
          left: bubbleLeft,
          maxWidth: 'calc(100vw - 2rem)',
          visibility: layout && settled ? 'visible' : 'hidden'
        }}
      >
        <GuideBubble
          title={current.title}
          body={current.body}
          placement={placement}
          arrowOffset={layout?.arrowOffset ?? 32}
          onNext={onNext}
          onSkip={onFinish}
          nextLabel={nextLabel}
          skipLabel={skipLabel}
        />
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.active
 * @param {() => void} props.onClose
 * @param {() => void} [props.onComplete]
 * @param {(key: string) => string} props.t
 * @param {{ anchor?: string, placement?: string, title: string, body: string, type?: string }[]} props.steps
 * @param {Record<string, React.RefObject>} props.anchorRefs
 * @param {(anchor: string | null) => void} [props.onAnchorChange]
 */
function PageTutorialGuide({ active, onClose, onComplete, t, steps, anchorRefs, onAnchorChange }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (active) setStep(0);
  }, [active]);

  useEffect(() => {
    if (!active || !onAnchorChange) return;
    const current = steps?.[step];
    const anchor = current?.type === 'welcome' || !current?.anchor ? null : current.anchor;
    onAnchorChange(anchor);
  }, [active, step, steps, onAnchorChange]);

  const finish = useCallback(() => {
    onClose();
  }, [onClose]);

  const complete = useCallback(() => {
    if (onComplete) onComplete();
    else onClose();
  }, [onClose, onComplete]);

  if (!active || !steps?.length) return null;

  const current = steps[step];
  const isLast = step >= steps.length - 1;

  const handleNext = () => {
    if (isLast) complete();
    else setStep((s) => s + 1);
  };

  const nextLabel = isLast ? t('test.guideDone') : t('test.guideNext');
  const skipLabel = t('test.guideSkip');

  if (current.type === 'welcome' || !current.anchor) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
        <div className="relative bg-surface rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h3 className="text-lg font-bold text-ink mb-2">{current.title}</h3>
          <p className="whitespace-pre-line leading-relaxed text-ink-muted">{current.body}</p>
          <div className="flex justify-end gap-2 mt-6">
            <button type="button" onClick={finish} className="px-3 py-2 text-sm text-ink-muted hover:text-ink">
              {skipLabel}
            </button>
            <button type="button" onClick={handleNext} className={testBtnPrimary}>
              {nextLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const anchorEl = anchorRefs?.[current.anchor]?.current;

  if (!anchorEl) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
        <div className="bg-surface rounded-2xl p-6 max-w-md shadow-xl">
          <h3 className="text-lg font-bold text-ink mb-2">{current.title}</h3>
          <p className="mb-4 whitespace-pre-line text-sm text-ink-muted">{current.body}</p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={finish} className="px-3 py-2 text-sm text-ink-muted">
              {skipLabel}
            </button>
            <button type="button" onClick={handleNext} className={testBtnPrimary}>
              {nextLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AnchoredTutorialStep
      key={`${step}-${current.anchor}`}
      anchorEl={anchorEl}
      current={current}
      onFinish={finish}
      onNext={handleNext}
      nextLabel={nextLabel}
      skipLabel={skipLabel}
    />
  );
}

export default PageTutorialGuide;
